// 浮出来的窗口(看板、查看器、侧栏、地块卡、生意页、股市……)共用的框:按住标题栏拖、右下角拉大小、
// 点哪个哪个到最上面、双击标题栏回到默认位置和大小;位置和大小按窗口种类记在这台浏览器上。
// 只动窗口元素自己的 style:里面的内容照旧由各页画(sandbox.ts 的 patch 只改子节点),重画不会把窗口挪回原位,滚动位置也不动。
// 窄屏(和 sandbox.css 的窄屏同一条线,760 像素)照原来铺满,不拖、不拉。地图的拖动缩放在画布上,这里不碰。

export interface WinOpts {
  /** 记位置用的名字,一种窗口一个 */
  kind: string;
  /** 标题栏:窗口里的哪一块能按住拖(里面的按钮、链接、输入框不算) */
  handle: string;
  /** 右下角能拉大小(拉手画在窗口的底色上,点中右下角 16 像素就算) */
  resize?: boolean;
  /** 默认上下撑满(看板、查看器、侧栏、股市、找房):没拉过大小的,挪到哪都从那里撑到屏幕底下 */
  fill?: boolean;
  /** 总在最上面(拍板卡、快捷键):不参与叠放次序 */
  top?: boolean;
  /** 显示、隐藏看哪个元素的 hidden(默认窗口自己;快捷键卡看外面那层遮罩) */
  toggle?: HTMLElement;
  /** 双击回到默认位置以后(地块卡要重新贴到点的那块地旁边) */
  onReset?: () => void;
  /** 窗口自己滚(股市、找房):标题栏的高度量好给 --sb-head-h,表头接着吸在它下面 */
  headVar?: boolean;
}

interface Geo { x: number; y: number; w?: number; h?: number }
interface Win { el: HTMLElement; o: WinOpts }

const KEY = 'sb:win';
const NARROW = '(max-width: 760px)';
/** 叠放次序从这里往上排(地图 0、顶栏 28、左边一列 27、快讯 26 都不动) */
const Z0 = 13;
/** 窗口至少留在屏幕里的宽度;标题栏至少露出的高度 */
const KEEP_W = 120, KEEP_H = 56, MIN_W = 260, MIN_H = 140, CORNER = 16, GAP = 12;

const wins: Win[] = [];
const order: HTMLElement[] = [];
let saved: Record<string, Geo> = {};
try {
  const v = JSON.parse(localStorage.getItem(KEY) ?? '{}');
  if (v && typeof v === 'object') saved = v;
} catch { /* 没存过、存不了:都按默认位置 */ }
const persist = () => {
  try { localStorage.setItem(KEY, JSON.stringify(saved)); } catch { /* 存不了就只管这一回 */ }
};

const narrow = () => window.matchMedia(NARROW).matches;
const shown = (w: Win) => !(w.o.toggle ?? w.el).hidden && !w.el.hidden;
/** 顶栏下沿:窗口的标题栏不躲到顶栏底下 */
const topEdge = () => (document.getElementById('top')?.getBoundingClientRect().bottom ?? 0) + 4;
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/** 按住了什么:按钮、链接、输入框、页签、带 data-* 的(这个沙盘里能点的都带)都不算标题栏 */
const interactive = (t: Element | null, stop: Element) => {
  for (let e = t; e && e !== stop; e = e.parentElement) {
    if (e.matches('a,button,input,select,textarea,label,summary,option,canvas,[role=button],[contenteditable],[tabindex]')) return true;
    if ((e as HTMLElement).dataset && Object.keys((e as HTMLElement).dataset).length) return true;
  }
  return false;
};

const STYLE_KEYS = ['position', 'left', 'top', 'right', 'bottom', 'width', 'height', 'maxHeight', 'margin', 'transform', 'translate'] as const;
const clearGeo = (el: HTMLElement) => { for (const k of STYLE_KEYS) el.style[k] = ''; };

/** 把窗口按 g 摆好(左上角 x、y;宽高只有拉过的才有)。出了屏幕的往回收。 */
const apply = (w: Win, g: Geo) => {
  const { el, o } = w;
  const W = window.innerWidth, H = window.innerHeight, t0 = topEdge();
  if (g.w != null) el.style.width = `${Math.round(clamp(g.w, MIN_W, W - 2 * GAP))}px`;
  const width = g.w != null ? parseFloat(el.style.width) : el.getBoundingClientRect().width;
  const x = clamp(g.x, KEEP_W - width, W - KEEP_W);
  const y = clamp(g.y, t0, Math.max(t0, H - KEEP_H));
  Object.assign(el.style, { position: 'fixed', left: `${Math.round(x)}px`, top: `${Math.round(y)}px`, right: 'auto', bottom: 'auto', margin: '0', transform: 'none' });
  // 底下留一点缝(和默认摆法一样 12 像素);太靠下的至少留 MIN_H 高,露出来多少算多少
  const room = Math.max(MIN_H, H - y - GAP);
  let max = room;
  if (!o.fill && g.h == null) {
    // 随内容高矮的卡片:样式表里的最高(比如七成屏高)照旧算数,只是不让它伸出屏幕底下
    el.style.maxHeight = '';
    const css = parseFloat(getComputedStyle(el).maxHeight);
    if (Number.isFinite(css)) max = Math.min(css, room);
  }
  el.style.maxHeight = `${Math.round(max)}px`;
  el.style.height = g.h != null ? `${Math.round(g.h)}px` : o.fill ? `${Math.round(room)}px` : '';
  return { x: Math.round(x), y: Math.round(y), width };
};

/** 叠放:最后点的、最后打开的在最上面 */
const restack = () => order.forEach((el, i) => { el.style.zIndex = String(Z0 + i); el.classList.toggle('sb-win-front', i === order.length - 1); });
export const raise = (el: HTMLElement) => {
  const i = order.indexOf(el);
  if (i < 0) return;
  if (i === order.length - 1) return void el.classList.add('sb-win-front');
  order.splice(i, 1);
  order.push(el);
  restack();
};

/** 窗口的位置是玩家摆的(拖过、拉过):地块卡就不再贴着点的位置挪。 */
export const placed = (el: HTMLElement) => {
  const w = wins.find((x) => x.el === el);
  return !!w && !narrow() && saved[w.o.kind] != null;
};

/** 里面重画以后:金色主按钮只留第一个,后面的标上 sb-2nd 照普通按钮画(一屏之内主操作最多一个,见 11-美术.md);
 * 标题栏的高度给吸顶的表头用。 */
const PRIMARY = '.sb-sd-btn.primary, .btn.primary';
const tidy = (w: Win) => {
  let first = true;
  for (const b of w.el.querySelectorAll<HTMLElement>(PRIMARY)) {
    if (b.classList.contains('sb-2nd') === first) b.classList.toggle('sb-2nd', !first);
    first = false;
  }
  if (!w.o.headVar) return;
  const v = `${w.el.querySelector<HTMLElement>(w.o.handle)?.offsetHeight ?? 0}px`;
  if (w.el.style.getPropertyValue('--sb-head-h') !== v) w.el.style.setProperty('--sb-head-h', v);
};

/** 拖、拉进行中:一个手势一份 */
let gesture: { w: Win; id: number; kind: 'move' | 'size'; sx: number; sy: number; r: DOMRect; on: boolean; dx: number; dy: number; raf: number } | null = null;
let swallowClick = false;

/** 拖到哪了(收在屏幕里) */
const target = (g: NonNullable<typeof gesture>) => {
  const W = window.innerWidth, H = window.innerHeight, t0 = topEdge();
  return { x: Math.round(clamp(g.r.left + g.dx, KEEP_W - g.r.width, W - KEEP_W)), y: Math.round(clamp(g.r.top + g.dy, t0, Math.max(t0, H - KEEP_H))) };
};
const frame = () => {
  const g = gesture;
  if (!g) return;
  g.raf = 0;
  const { el } = g.w;
  if (g.kind === 'move') {
    // 拖的时候只挪图层(translate,和打开时的淡入动画各管各的),松手再写回 left/top:每帧不用重新排版
    const { x, y } = target(g);
    el.style.translate = `${x - Math.round(g.r.left)}px ${y - Math.round(g.r.top)}px`;
  } else {
    // 拉大小:左上角不动,宽高跟手,不出屏幕
    const { w, h } = size(g);
    el.style.width = `${w}px`;
    el.style.height = `${h}px`;
    el.style.maxHeight = `${h}px`;
  }
};
/** 拉到多大了 */
const size = (g: NonNullable<typeof gesture>) => ({
  w: Math.round(clamp(g.r.width + g.dx, MIN_W, Math.max(MIN_W, window.innerWidth - g.r.left - GAP))),
  // 本来就比最矮还矮的卡片(只有一两行)按住角不会先跳高
  h: Math.round(clamp(g.r.height + g.dy, Math.min(MIN_H, g.r.height), Math.max(MIN_H, window.innerHeight - g.r.top - GAP))),
});

const move = (e: PointerEvent) => {
  const g = gesture;
  if (!g || e.pointerId !== g.id) return;
  // 鼠标在浏览器外面松开、没收到 pointerup 的:回来时按钮已经不按着了,当作松手
  if (e.pointerType === 'mouse' && !(e.buttons & 1)) return finish(g, false);
  g.dx = e.clientX - g.sx;
  g.dy = e.clientY - g.sy;
  if (!g.on) {
    if (Math.abs(g.dx) + Math.abs(g.dy) < 4) return;
    // 动了才算拖:先把窗口换成自己的 left/top(默认摆法各式各样:贴右边、贴底下、居中),再接着跟手
    g.on = true;
    const { el } = g.w;
    const cur = saved[g.w.o.kind];
    const sizing = g.kind === 'size';
    const at = apply(g.w, { x: g.r.left, y: g.r.top, w: sizing || cur?.w != null ? g.r.width : undefined, h: sizing || cur?.h != null ? g.r.height : undefined });
    g.r = new DOMRect(at.x, at.y, at.width, el.offsetHeight);
    try { el.setPointerCapture(g.id); } catch { /* 指针已经没了 */ }
    el.classList.add(g.kind === 'move' ? 'sb-win-moving' : 'sb-win-sizing');
    document.documentElement.classList.add(g.kind === 'move' ? 'sb-dragging' : 'sb-sizing');
    window.getSelection()?.removeAllRanges();
  }
  e.preventDefault();
  if (!g.raf) g.raf = requestAnimationFrame(frame);
};

const end = (e: PointerEvent) => {
  const g = gesture;
  if (g && e.pointerId === g.id) finish(g, e.type === 'pointercancel');
};
/** 收手:cancel 是浏览器收走了(触摸变成滚动)或者手势作废,窗口回到按下时的样子 */
const finish = (g: NonNullable<typeof gesture>, cancel: boolean) => {
  gesture = null;
  window.removeEventListener('pointermove', move);
  window.removeEventListener('pointerup', end);
  window.removeEventListener('pointercancel', end);
  if (g.raf) cancelAnimationFrame(g.raf);
  const { el, o } = g.w;
  if (!g.on) return;
  el.classList.remove('sb-win-moving', 'sb-win-sizing');
  document.documentElement.classList.remove('sb-dragging', 'sb-sizing');
  try { el.releasePointerCapture(g.id); } catch { /* 已经放了 */ }
  // 写回:挪的记新的左上角,拉的记宽高(触摸被浏览器收走的那种算没动)
  const prev = saved[o.kind];
  const geo: Geo = g.kind === 'move' && !cancel ? target(g) : { x: Math.round(g.r.left), y: Math.round(g.r.top) };
  if (g.kind === 'size') {
    const { w, h } = cancel ? { w: Math.round(g.r.width), h: Math.round(g.r.height) } : size(g);
    geo.w = w; geo.h = h;
  } else {
    if (prev?.w != null) geo.w = prev.w;
    if (prev?.h != null) geo.h = prev.h;
  }
  el.style.translate = '';
  saved[o.kind] = geo;
  persist();
  apply(g.w, geo);
  // 松手那下的 click 别落到别的东西上
  if (cancel) return;
  swallowClick = true;
  setTimeout(() => { swallowClick = false; }, 0);
};

const corner = (el: HTMLElement, x: number, y: number) => {
  const r = el.getBoundingClientRect();
  return x > r.right - CORNER && y > r.bottom - CORNER && x <= r.right && y <= r.bottom;
};

const down = (w: Win) => (e: PointerEvent) => {
  const { el, o } = w;
  raise(el);
  if (narrow() || (e.pointerType === 'mouse' && e.button !== 0)) return;
  // 上一个手势没收到松手(指针丢了):作废,别把以后的拖拉都挡住
  if (gesture) finish(gesture, true);
  const t = e.target as Element;
  let kind: 'move' | 'size' | null = null;
  if (o.resize && corner(el, e.clientX, e.clientY)) kind = 'size';
  else {
    const h = t.closest(o.handle);
    if (h && el.contains(h) && !interactive(t, h)) kind = 'move';
  }
  if (!kind) return;
  // 鼠标按下就不让它选字;触摸靠标题栏上的 touch-action: none
  if (e.pointerType === 'mouse') e.preventDefault();
  gesture = { w, id: e.pointerId, kind, sx: e.clientX, sy: e.clientY, r: el.getBoundingClientRect(), on: false, dx: 0, dy: 0, raf: 0 };
  window.addEventListener('pointermove', move, { passive: false });
  window.addEventListener('pointerup', end);
  window.addEventListener('pointercancel', end);
};

/** 双击标题栏:回到默认位置和大小 */
const reset = (w: Win) => (e: MouseEvent) => {
  if (narrow()) return;
  const t = e.target as Element;
  const h = t.closest(w.o.handle);
  if (!h || !w.el.contains(h) || interactive(t, h)) return;
  delete saved[w.o.kind];
  persist();
  clearGeo(w.el);
  w.o.onReset?.();
};

/** 鼠标停在右下角:换成拉大小的光标 */
const hover = (w: Win) => (e: PointerEvent) => {
  if (gesture || e.pointerType !== 'mouse') return;
  const on = !narrow() && corner(w.el, e.clientX, e.clientY);
  if (w.el.classList.contains('sb-win-corner') !== on) w.el.classList.toggle('sb-win-corner', on);
};

/** 打开的时候:摆回记下的位置,放到最上面 */
const opened = (w: Win) => {
  const g = saved[w.o.kind];
  if (g && !narrow()) apply(w, g);
  if (!w.o.top) raise(w.el);
};

/** 把一个元素接成窗口。 */
export function makeWin(el: HTMLElement, o: WinOpts) {
  const w: Win = { el, o };
  wins.push(w);
  if (!o.top) { order.push(el); restack(); }
  el.classList.add('sb-win');
  if (o.resize) {
    el.classList.add('sb-win-rs');
    el.addEventListener('pointermove', hover(w));
    el.addEventListener('pointerleave', () => el.classList.remove('sb-win-corner'));
  }
  el.addEventListener('pointerdown', down(w));
  el.addEventListener('dblclick', reset(w));
  // 开关看 hidden;里面重画了(子节点变了)整理一下
  let was = shown(w);
  const mo = new MutationObserver((list) => {
    const now = shown(w);
    if (now && !was) opened(w);
    was = now;
    if (now && list.some((m) => m.target !== el || m.type === 'childList')) tidy(w);
  });
  mo.observe(el, { attributes: true, attributeFilter: ['hidden', 'class'], childList: true, subtree: true });
  if (o.toggle && o.toggle !== el) mo.observe(o.toggle, { attributes: true, attributeFilter: ['hidden'] });
  if (was) opened(w);
  tidy(w);
  return w;
}

// 屏幕大小变了:摆过的窗口收回屏幕里;过了窄屏那条线就交还给样式表铺满
let resizeRaf = 0;
const refit = () => {
  resizeRaf = 0;
  const n = narrow();
  for (const w of wins) {
    const g = saved[w.o.kind];
    if (!g) continue;
    if (n) clearGeo(w.el);
    else if (shown(w)) apply(w, g);
  }
};
window.addEventListener('resize', () => { if (!resizeRaf) resizeRaf = requestAnimationFrame(refit); });
// 拖完松手的那一下 click 不算点
document.addEventListener('click', (e) => { if (swallowClick) { swallowClick = false; e.stopPropagation(); e.preventDefault(); } }, true);
